import { createSlice } from '@reduxjs/toolkit';
import {
    mergeUniqueByKey,
    sortTransactionsByIdDesc,
} from '../../components/commonLayouts/ListBarDetailLayout/ListBar/ListBar.helpers';
import {
    gettransactionByID,
    getAlltransactionOps,
    updateTransactionDeal,
    generateSOPO,
    getBDNsDetails,
    getsupplierInvoiceByOps,
    saveBDNBySup,
    saveSupplierInvoiceBySup,
    uploadBDNBySup,
    uploadSupInviceBySup,
    saveBuyerInvoiceBySup,
    getBuyerInvoiceByOps,
    uploadBuyerInviceBySup,
    getPayment,
    getTotalCODAmount,
    getTotalMTTraded,
    dealfinancing,
    platformCharges,
    getTransactionsAddOnServiceDetails,
    getAssignPOCLists,
    GetAgentDetails,
    createPayment,
    dealclosure,
    getDealCalculation,
    getFinanceCostSimulator,
    cancelTransaction,
    uploadDeliveryTrackingDocuments,
} from '../actions/transactionApi';

function millisecondsToDHM(milliseconds) {
    // Convert milliseconds to seconds
    let seconds = Math.abs(milliseconds) / 1000;

    // Calculate days
    const days = Math.floor(seconds / (24 * 3600));
    seconds -= days * 24 * 3600;

    // Calculate hours
    const hours = Math.floor(seconds / 3600) % 24;
    seconds -= hours * 3600;

    // Calculate minutes
    const minutes = Math.floor(seconds / 60) % 60;

    return { days, hours, minutes };
}

export const transactionSlice = createSlice({
    name: 'transaction',
    initialState: {
        item: {},
        isLoading: false,
        error: null,
        alltransaction: [],
        updateTransactionDealPayload: {},
        isUpdateTransactionDeal: false,
        generateSOPOPayload: {},
        bdnsDetails: {},
        supInvoiceDetails: {},
        buyerInvoiceDetails: {},
        isSuccessMsg: null,
        isSuccess: false,
        isUploadedBuyerInvice: false,
        paymentsList: [],
        isMilestoneUpdating: false,
        totalCODAmount: {},
        totalMTTraded: {},
        platformCharges: [],
        calculatedPaymentStatus: '',
        calculatedOverDuedays: {},
        addOnServicesDetails: [],
        assignedPocList: [],
        agentDetails: [],
        buyerSupplierPaymentStatus: 'Due',
        paymentOverdueBy: null,
        dealCalculation: [],
        financeCostSimulator: [],
        isListLoadingMore: false,
    },
    reducers: {
        paymentStatusFun: (state, action) => {
            const due_dateCompare = new Date(action.payload.date1);
            const payment_dateCompare = new Date(action.payload.date2);
            const currentDate = new Date();

            if (action.payload.date2 && action.payload.date2 !== '0001-01-01T00:00:00Z') {
                if (payment_dateCompare - due_dateCompare <= 0) {
                    state.calculatedPaymentStatus = 'Paid (On time)';
                } else if (payment_dateCompare - due_dateCompare > 0) {
                    state.calculatedPaymentStatus = 'Paid (Past Due)';
                }
            } else {
                if (due_dateCompare.setHours(0, 0, 0, 0) >= currentDate.setHours(0, 0, 0, 0)) {
                    state.calculatedPaymentStatus = 'Payment Due';
                } else if (due_dateCompare.setHours(0, 0, 0, 0) < currentDate.setHours(0, 0, 0, 0)) {
                    state.calculatedPaymentStatus = 'Overdue';
                } else {
                    state.calculatedPaymentStatus = '';
                }
            }
        },
        overdueByCal: (state, action) => {
            const due_date = new Date(action.payload.dueDate);
            const payment_date = new Date(action.payload.paydate);
            const currentDate = new Date();
            const overdueDays = currentDate - due_date;
            const pastdueDays = payment_date - due_date;

            if (state.calculatedPaymentStatus === 'Overdue' && overdueDays) {
                state.calculatedOverDuedays = millisecondsToDHM(overdueDays);
            } else if (state.calculatedPaymentStatus === 'Paid (On time)') {
                state.calculatedOverDuedays = { days: '-', hours: '-', minutes: '-' };
            } else if (state.calculatedPaymentStatus === 'Paid (Past Due)' && pastdueDays) {
                state.calculatedOverDuedays = millisecondsToDHM(pastdueDays);
            } else {
                state.calculatedOverDuedays = { days: '-', hours: '-', minutes: '-' };
            }
        },
        calculatePaymentStatus: (state, action) => {
            // Convert dates to Date objects for comparison
            const { dueDate, invoiceAmount, payments } = action.payload;
            const dueDateObj = new Date(dueDate);
            const currentDateObj = new Date();
            // Find the latest payment date
            const lastPaymentDate = payments.reduce((latest, payment) => {
                const paymentDate = new Date(payment.released_on);
                return paymentDate > latest ? paymentDate : latest;
            }, new Date(0)); // Initialize with an old date
            const lastPaymentDateObj = lastPaymentDate ? new Date(lastPaymentDate) : null;

            // Calculate total paid amount (including both positive and negative amounts)
            const totalPaidAmount = payments.reduce((total, payment) => total + payment.amount, 0);

            // Calculate total outstanding amount
            const outstandingAmount = totalPaidAmount ? invoiceAmount - totalPaidAmount : invoiceAmount;
            // Determine the payment status
            if (outstandingAmount === 0) {
                if (lastPaymentDateObj && lastPaymentDateObj > dueDateObj) {
                    state.buyerSupplierPaymentStatus = 'Paid (Past Due)';
                } else {
                    state.buyerSupplierPaymentStatus = 'Paid';
                }
            } else if (currentDateObj > dueDateObj) {
                const overdueTimeMs = currentDateObj - dueDateObj;
                // Convert to days or hours
                const overdueDays = Math.floor(overdueTimeMs / (1000 * 60 * 60 * 24));
                if (overdueDays === 0) {
                    state.buyerSupplierPaymentStatus = 'Due';
                } else {
                    state.buyerSupplierPaymentStatus = 'Overdue';
                }
                state.paymentOverdueBy = `${overdueDays} days`;
            } else {
                state.buyerSupplierPaymentStatus = 'Due';
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(gettransactionByID.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(gettransactionByID.fulfilled, (state, action) => {
                if (
                    action.payload.isUpdateRequired &&
                    state.item.current_milestone === action.payload.payload.current_milestone
                ) {
                    state.isMilestoneUpdating = true;
                } else {
                    state.isMilestoneUpdating = false;
                }
                state.isLoading = false;

                state.item = action.payload.payload;
            })
            .addCase(gettransactionByID.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.item = {};
            })
            .addCase(getAlltransactionOps.pending, (state, action) => {
                const isAppend = Boolean(action.meta?.arg?.append);
                if (!isAppend) {
                    state.isLoading = true;
                }
                state.isListLoadingMore = isAppend;
            })
            .addCase(getAlltransactionOps.fulfilled, (state, action) => {
                const incomingTotal = Number(action.payload?.nextPage?.totalCount);
                const previousTotal = Number(state.alltransaction?.nextPage?.totalCount);
                const isNewQuery = Number(action.payload?.requestedCursor) === 0 && !action.payload?.append;
                const stableTotal = isNewQuery
                    ? incomingTotal
                    : Number.isFinite(previousTotal) && previousTotal > 0
                      ? previousTotal
                      : incomingTotal;
                const resolvedTotal = Number.isFinite(stableTotal) && stableTotal > 0 ? stableTotal : incomingTotal;

                state.isLoading = false;
                state.isListLoadingMore = false;

                const incoming = action.payload?.resp || [];
                const nextPage = {
                    ...(action.payload?.nextPage || {}),
                    totalCount: resolvedTotal,
                };

                // The dashboard replaces `resp` on every page change, so the side panel would lose
                // the earlier pages. `loadedResp` keeps every page fetched for the current query.
                const previousLoaded = Array.isArray(state.alltransaction?.loadedResp)
                    ? state.alltransaction.loadedResp
                    : state.alltransaction?.resp || [];
                const loadedResp =
                    isNewQuery || action.payload?.reset
                        ? incoming
                        : sortTransactionsByIdDesc(mergeUniqueByKey(previousLoaded, incoming, 'transaction'));

                if (action.payload?.append) {
                    // Side-panel paging grows the accumulated list only, so `resp` stays the page
                    // the dashboard table renders and its row count keeps matching its paginator.
                    state.alltransaction = {
                        resp: state.alltransaction?.resp || [],
                        loadedResp,
                        nextPage: {
                            ...nextPage,
                            has_next:
                                incoming.length === 0 || loadedResp.length === previousLoaded.length
                                    ? false
                                    : nextPage.has_next,
                        },
                    };
                } else {
                    state.alltransaction = {
                        resp: incoming,
                        loadedResp,
                        nextPage,
                    };
                }
                state.hasPortNext = state.alltransaction.nextPage?.has_next;
                state.nextPortCursor = state.alltransaction.nextPage?.next_cursor;
            })
            .addCase(getAlltransactionOps.rejected, (state, action) => {
                state.isLoading = false;
                state.isListLoadingMore = false;
                state.error = action.payload;
            })
            .addCase(updateTransactionDeal.pending, (state) => {
                state.isLoading = true;
                state.isUpdateTransactionDeal = false;
            })
            .addCase(updateTransactionDeal.fulfilled, (state, action) => {
                state.isLoading = false;
                state.updateTransactionDealPayload = action.payload;
                state.isUpdateTransactionDeal = true;
            })
            .addCase(updateTransactionDeal.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.isUpdateTransactionDeal = false;
            })
            .addCase(generateSOPO.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(generateSOPO.fulfilled, (state, action) => {
                state.isLoading = false;
                state.generateSOPOPayload = action.payload;
            })
            .addCase(generateSOPO.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(getBDNsDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getBDNsDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.bdnsDetails = action.payload.payload || {};
            })
            .addCase(getBDNsDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.bdnsDetails = {}; // empty object on error
            })
            .addCase(saveBDNBySup.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(saveBDNBySup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isSuccessMsg = action.payload;
            })
            .addCase(saveBDNBySup.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
            })
            .addCase(getsupplierInvoiceByOps.pending, (state, action) => {
                state.isLoading = false;
            })
            .addCase(getsupplierInvoiceByOps.fulfilled, (state, action) => {
                state.isLoading = false;
                state.supInvoiceDetails = action.payload;
            })
            .addCase(getsupplierInvoiceByOps.rejected, (state, action) => {
                state.isLoading = false;
                state.supInvoiceDetails = {};
            })
            .addCase(saveSupplierInvoiceBySup.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(saveSupplierInvoiceBySup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isSuccessMsg = action.payload;
            })
            .addCase(saveSupplierInvoiceBySup.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
            })
            .addCase(uploadSupInviceBySup.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(uploadSupInviceBySup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isSuccessMsg = action.payload;
            })
            .addCase(uploadSupInviceBySup.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
            })
            .addCase(cancelTransaction.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(cancelTransaction.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isSuccessMsg = action.payload;
                state.isMilestoneUpdating = false;
            })
            .addCase(cancelTransaction.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
            })
            .addCase(uploadDeliveryTrackingDocuments.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(uploadDeliveryTrackingDocuments.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isSuccessMsg = action.payload;
            })
            .addCase(uploadDeliveryTrackingDocuments.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
            })
            .addCase(uploadBDNBySup.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(uploadBDNBySup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isSuccessMsg = action.payload;
            })
            .addCase(uploadBDNBySup.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
            })
            .addCase(uploadBuyerInviceBySup.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
                state.isUploadedBuyerInvice = false;
            })
            .addCase(uploadBuyerInviceBySup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isSuccessMsg = action.payload;
                state.isUploadedBuyerInvice = true;
            })
            .addCase(uploadBuyerInviceBySup.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
                state.isUploadedBuyerInvice = false;
            })
            .addCase(getBuyerInvoiceByOps.pending, (state, action) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(getBuyerInvoiceByOps.fulfilled, (state, action) => {
                state.isLoading = false;
                state.buyerInvoiceDetails = action.payload;
            })
            .addCase(getBuyerInvoiceByOps.rejected, (state, action) => {
                state.isLoading = false;
                state.isSuccess = false;
                state.buyerInvoiceDetails = {};
            })
            .addCase(saveBuyerInvoiceBySup.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(saveBuyerInvoiceBySup.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.isSuccessMsg = action.payload;
            })
            .addCase(saveBuyerInvoiceBySup.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
                state.isUploadedBuyerInvice = false;
            })
            .addCase(getPayment.fulfilled, (state, action) => {
                state.paymentsList = action.payload;
            })
            .addCase(getPayment.rejected, (state, action) => {
                state.paymentsList = [];
            })
            .addCase(getTotalCODAmount.fulfilled, (state, action) => {
                state.totalCODAmount = action.payload;
            })
            .addCase(getTotalMTTraded.fulfilled, (state, action) => {
                state.totalMTTraded = action.payload;
            })
            .addCase(dealfinancing.fulfilled, (state, action) => {})
            .addCase(platformCharges.fulfilled, (state, action) => {
                state.platformCharges = action.payload;
            })
            .addCase(getTransactionsAddOnServiceDetails.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(getTransactionsAddOnServiceDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.addOnServicesDetails = action.payload.payload;
            })
            .addCase(getTransactionsAddOnServiceDetails.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
                state.addOnServicesDetails = [];
            })
            .addCase(getAssignPOCLists.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(getAssignPOCLists.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.assignedPocList = action.payload.payload;
            })
            .addCase(getAssignPOCLists.rejected, (state) => {
                state.isLoading = false;
                state.isSuccess = false;
                state.assignedPocList = [];
            })
            .addCase(GetAgentDetails.pending, (state) => {
                state.isLoading = true;
                state.isSuccess = false;
            })
            .addCase(GetAgentDetails.fulfilled, (state, action) => {
                state.agentDetails = action.payload;
            })
            .addCase(GetAgentDetails.rejected, (state) => {
                state.agentDetails = [];
            })
            .addCase(createPayment.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createPayment.fulfilled, (state, action) => {
                state.isLoading = false;
            })
            .addCase(createPayment.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(dealclosure.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(dealclosure.fulfilled, (state, action) => {
                state.isLoading = false;
            })
            .addCase(dealclosure.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(getDealCalculation.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getDealCalculation.fulfilled, (state, action) => {
                state.isLoading = false;
                state.dealCalculation = action.payload;
            })
            .addCase(getDealCalculation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(getFinanceCostSimulator.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getFinanceCostSimulator.fulfilled, (state, action) => {
                state.isLoading = false;
                state.financeCostSimulator = action.payload;
            })
            .addCase(getFinanceCostSimulator.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});
export const { paymentStatusFun, overdueByCal, calculatePaymentStatus } = transactionSlice.actions;
export default transactionSlice.reducer;
